(function (root) {
  "use strict";

  if (root.AudiocolourFrequencyPitchAudio) return;

  var LIMITS = Object.freeze({
    minFrequency: 20,
    maxFrequency: 20000,
    nyquistRatio: 0.45,
    assumedSampleRate: 44100,
    maxPartials: 32,
    maxMasterGain: 0.25,
    defaultVolume: 40,
    autoStopMs: 60000,
    attackMs: 15,
    releaseMs: 15,
    releaseDisconnectTimeoutMs: 100,
    retuneMs: 10,
    resumeTimeoutMs: 1000,
    gainSumTolerance: 1e-9
  });
  var SOURCE_ID = /^[a-z0-9-]{1,64}$/;
  var clients = new Map();
  var nextClientId = 0;
  var nextPlaybackId = 0;
  var active = null;
  var releasing = null;
  var context = null;
  var lastContextState = "none";
  var lastKnownSampleRate = null;
  var sampleRate = null;
  var provisional = true;
  var eventQueue = [];
  var eventScheduled = false;
  var visibilityInstalled = false;

  function finite(value) { return typeof value === "number" && Number.isFinite(value); }
  function resultError(code, message, details) {
    return { ok: false, error: Object.assign({ code: code, message: message }, details || {}) };
  }
  function success(value) { return Object.assign({ ok: true }, value || {}); }
  function makeError(code, message, details) {
    return Object.assign({ code: code, message: message }, details || {});
  }
  function contextConstructor() {
    return root.AudioContext || root.webkitAudioContext || null;
  }
  function isSupported() { return typeof contextConstructor() === "function"; }
  function maxPlayable() {
    return provisional || !finite(sampleRate)
      ? LIMITS.maxFrequency
      : Math.min(LIMITS.maxFrequency, LIMITS.nyquistRatio * sampleRate);
  }
  function deviceSnapshot() {
    return {
      sampleRate: provisional ? null : sampleRate,
      provisional: provisional,
      maxPlayableFrequency: maxPlayable(),
      estimatedDeviceLimit: provisional
        ? LIMITS.nyquistRatio * LIMITS.assumedSampleRate
        : null
    };
  }
  function checkFrequency(frequency) {
    var failed;
    if (!finite(frequency) || frequency <= 0) {
      failed = makeError("invalid_frequency", "Frequency must be finite and positive.", {
        limit: null, provisional: provisional
      });
    } else if (frequency < LIMITS.minFrequency) {
      failed = makeError("below_min_frequency", "Frequency is below the playback minimum.", {
        limit: LIMITS.minFrequency, provisional: provisional
      });
    } else if (frequency > LIMITS.maxFrequency) {
      failed = makeError("above_max_frequency", "Frequency exceeds the fixed playback maximum.", {
        limit: LIMITS.maxFrequency, provisional: provisional
      });
    } else if (!provisional && frequency > maxPlayable()) {
      failed = makeError("above_device_limit", "Frequency exceeds the actual device limit.", {
        limit: maxPlayable(), provisional: false, sampleRate: sampleRate
      });
    }
    if (failed) return { ok: false, error: failed };
    return {
      ok: true,
      provisional: provisional,
      sampleRate: provisional ? null : sampleRate,
      maxPlayableFrequency: maxPlayable(),
      advisory: provisional && frequency > LIMITS.nyquistRatio * LIMITS.assumedSampleRate
        ? { code: "may_exceed_device_limit", estimatedLimit: LIMITS.nyquistRatio * LIMITS.assumedSampleRate }
        : null
    };
  }
  function queueMicrotaskSafe(callback) {
    if (typeof root.queueMicrotask === "function") root.queueMicrotask(callback);
    else Promise.resolve().then(callback);
  }
  function scheduleEventDrain() {
    if (eventScheduled) return;
    eventScheduled = true;
    queueMicrotaskSafe(drainEvents);
  }
  function queueEvent(client, event, after) {
    eventQueue.push({ client: client || null, event: event, after: after || null });
    scheduleEventDrain();
  }
  function drainEvents() {
    eventScheduled = false;
    while (eventQueue.length) {
      var item = eventQueue.shift();
      if (!item.client || !item.client.destroyed) {
        if (item.client && item.client.onChange) {
          try { item.client.onChange(item.event); } catch (_) { /* Isolate consumer callbacks. */ }
        }
      }
      if (item.after) {
        try { item.after(); } catch (_) { /* Promise settlement must not break the event queue. */ }
      }
    }
    if (eventQueue.length) scheduleEventDrain();
  }
  function sendDeviceEvent() {
    var snapshot = deviceSnapshot();
    var event = {
      type: "device",
      sampleRate: snapshot.sampleRate,
      provisional: snapshot.provisional,
      maxPlayableFrequency: snapshot.maxPlayableFrequency,
      estimatedDeviceLimit: snapshot.estimatedDeviceLimit
    };
    clients.forEach(function (client) {
      if (!client.destroyed) queueEvent(client, event);
    });
  }
  function playbackEvent(playback, type, fields, after) {
    var event = {
      type: type,
      clientId: playback.client.id,
      playbackId: playback.playbackId,
      sourceId: playback.sourceId,
      kind: playback.kind,
      frequency: playback.kind === "tone" ? playback.frequency : null,
      partialCount: playback.kind === "partials" ? playback.partials.length : 1,
      reason: null,
      error: null,
      replacedBy: null
    };
    Object.assign(event, fields || {});
    queueEvent(playback.client, event, after);
  }
  function setDeviceRate(rate) {
    if (sampleRate === rate && !provisional) return false;
    sampleRate = rate;
    lastKnownSampleRate = rate;
    provisional = false;
    sendDeviceEvent();
    return true;
  }
  function resetDeviceRate(forceEvent) {
    var changed = !(provisional && sampleRate === null);
    sampleRate = null;
    provisional = true;
    if (changed || forceEvent) sendDeviceEvent();
  }
  function setTimer(callback, delay) {
    return root.setTimeout(callback, delay);
  }
  function clearTimer(timer) {
    if (timer !== null && timer !== undefined) root.clearTimeout(timer);
  }
  function now() {
    return context && finite(context.currentTime) ? context.currentTime : 0;
  }
  function ramp(param, from, to, durationMs, at) {
    if (!param) return;
    var start = finite(at) ? at : now();
    var duration = durationMs / 1000;
    try {
      if (typeof param.cancelScheduledValues === "function") param.cancelScheduledValues(start);
      if (typeof param.setValueAtTime === "function") param.setValueAtTime(from, start);
      else param.value = from;
      if (durationMs <= 0) {
        if (typeof param.setValueAtTime === "function") param.setValueAtTime(to, start);
        else param.value = to;
      } else if (typeof param.linearRampToValueAtTime === "function") {
        param.linearRampToValueAtTime(to, start + duration);
      } else {
        param.value = to;
      }
    } catch (_) {
      try { param.value = to; } catch (_) { /* AudioParam may be detached. */ }
    }
  }
  function volumeGain(volume) {
    var bounded = Math.max(0, Math.min(100, volume));
    return LIMITS.maxMasterGain * Math.pow(bounded / 100, 2);
  }
  function makePlayFailure(playback, stage, code, message, details) {
    return {
      ok: false,
      playbackId: playback.playbackId,
      sourceId: playback.sourceId,
      kind: playback.kind,
      stage: stage,
      error: makeError(code, message, details)
    };
  }
  function resolvePlayback(playback, value) {
    if (playback.settled) return;
    playback.settled = true;
    playback.resolve(value);
  }
  function clearStartupTimer(playback) {
    if (playback.resumeTimer !== null) {
      clearTimer(playback.resumeTimer);
      playback.resumeTimer = null;
    }
    if (playback.cancelWait) {
      var cancel = playback.cancelWait;
      playback.cancelWait = null;
      cancel();
    }
  }
  function isCurrent(playback) {
    return active === playback && !playback.terminal && !playback.client.destroyed;
  }
  function markTerminal(playback) {
    playback.terminal = true;
    clearStartupTimer(playback);
  }
  function reasonError(reason, cause) {
    var details = {};
    if (cause) details.cause = String(cause && cause.message || cause);
    if (reason === "playback_blocked") {
      details.contextState = context ? context.state : lastContextState;
      details.timedOut = !!(cause && cause.timeout);
    }
    return makeError(reason, "Playback could not start: " + reason + ".", details);
  }
  function failStarting(playback, reason, details) {
    if (playback.terminal) return;
    if (active === playback) active = null;
    markTerminal(playback);
    var stage = reason === "client_destroyed" ? "startup" : "startup";
    var failure = makePlayFailure(playback, stage, reason, "Playback could not start: " + reason + ".", details);
    if (playback.client.destroyed || reason === "client_destroyed") {
      resolvePlayback(playback, failure);
      return;
    }
    playbackEvent(playback, "stopped", {
      reason: reason,
      error: failure.error
    }, function () { resolvePlayback(playback, failure); });
  }
  function cancelStarting(playback, reason, replacement) {
    if (playback.terminal) return;
    markTerminal(playback);
    if (active === playback) active = null;
    var details = replacement ? { supersededBy: replacement } : {};
    var failure = makePlayFailure(playback, "startup", reason, "Playback ended before it started: " + reason + ".", details);
    if (playback.client.destroyed || reason === "client_destroyed") {
      resolvePlayback(playback, failure);
      return;
    }
    playbackEvent(playback, "stopped", {
      reason: reason,
      error: failure.error,
      replacedBy: replacement || null
    }, function () { resolvePlayback(playback, failure); });
  }
  function frequencyList(playback) {
    return playback.kind === "tone"
      ? [playback.frequency]
      : playback.partials.map(function (partial) { return partial.frequency; });
  }
  function validatePlay(kind, request) {
    var sourceId = request && typeof request.sourceId === "string" ? request.sourceId : null;
    var playback = { kind: kind, sourceId: sourceId };
    if (!request || typeof request !== "object" || Array.isArray(request)) {
      return { error: makeError("invalid_request", "Request must be an object.", { field: "request" }), playback: playback };
    }
    if (typeof request.sourceId !== "string" || !SOURCE_ID.test(request.sourceId)) {
      return { error: makeError("invalid_source_id", "Source ID has an invalid format."), playback: playback };
    }
    if (kind === "tone") {
      var frequencyCheck = checkFrequency(request.frequency);
      if (!frequencyCheck.ok) return { error: frequencyCheck.error, playback: playback };
      playback.frequency = request.frequency;
      return { playback: playback };
    }
    if (!Array.isArray(request.partials)) {
      return { error: makeError("invalid_request", "Partials must be an array.", { field: "partials" }), playback: playback };
    }
    if (request.partials.length === 0) {
      return { error: makeError("no_partials", "At least one partial is required."), playback: playback };
    }
    if (request.partials.length > LIMITS.maxPartials) {
      return { error: makeError("too_many_partials", "Partial count exceeds the engine limit.", { limit: LIMITS.maxPartials }), playback: playback };
    }
    var seen = new Map();
    var sum = 0;
    var copied = [];
    for (var i = 0; i < request.partials.length; i += 1) {
      var partial = request.partials[i];
      if (!partial || typeof partial !== "object" || Array.isArray(partial)) {
        return { error: makeError("invalid_request", "Partial must be an object.", { field: "partials[" + i + "]", partialIndex: i }), playback: playback };
      }
      var check = checkFrequency(partial.frequency);
      if (!check.ok) {
        return { error: Object.assign({}, check.error, { partialIndex: i }), playback: playback };
      }
      if (!finite(partial.gain) || partial.gain < 0 || partial.gain > 1) {
        return { error: makeError("invalid_partial_gain", "Partial gain must be finite and between 0 and 1.", { partialIndex: i }), playback: playback };
      }
      if (seen.has(partial.frequency)) {
        return { error: makeError("duplicate_partial_frequency", "Partial frequencies must be unique.", {
          partialIndex: i, duplicateOf: seen.get(partial.frequency)
        }), playback: playback };
      }
      seen.set(partial.frequency, i);
      sum += partial.gain;
      copied.push({ frequency: partial.frequency, gain: partial.gain });
    }
    if (sum > 1 + LIMITS.gainSumTolerance) {
      return { error: makeError("partial_gain_sum_exceeded", "Partial gains sum above the allowed limit.", {
        sum: sum, limit: 1, tolerance: LIMITS.gainSumTolerance
      }), playback: playback };
    }
    if (sum <= 0) {
      return { error: makeError("zero_total_gain", "At least one partial gain must be greater than zero."), playback: playback };
    }
    if (sum > 1) copied = copied.map(function (part) { return { frequency: part.frequency, gain: part.gain / sum }; });
    playback.partials = copied;
    return { playback: playback };
  }
  function makeContext() {
    if (context && context.state !== "closed") {
      if (context.state === "running") return { context: context, error: null, resumePromise: Promise.resolve() };
      try {
        if (typeof context.resume !== "function") return { context: context, error: makeError("playback_blocked", "AudioContext cannot resume."), resumePromise: null };
        return { context: context, error: null, resumePromise: context.resume() };
      } catch (error) {
        return { context: context, error: makeError("playback_blocked", "AudioContext resume failed.", { cause: String(error && error.message || error) }), resumePromise: null };
      }
    }
    if (context && context.state === "closed") {
      removeContextListener(context);
      context = null;
      resetDeviceRate(true);
      lastContextState = "closed";
    }
    var Constructor = contextConstructor();
    if (typeof Constructor !== "function") return { context: null, error: makeError("unsupported", "Web Audio is unavailable."), resumePromise: null };
    try {
      context = new Constructor();
      lastContextState = context.state || "suspended";
      installContextListener(context);
      if (context.state === "running") return { context: context, error: null, resumePromise: Promise.resolve() };
      if (typeof context.resume !== "function") return { context: context, error: makeError("playback_blocked", "AudioContext cannot resume."), resumePromise: null };
      try {
        var resumePromise = context.resume();
        return { context: context, error: null, resumePromise: resumePromise };
      } catch (resumeError) {
        return { context: context, error: makeError("playback_blocked", "AudioContext resume failed.", { cause: String(resumeError && resumeError.message || resumeError) }), resumePromise: null };
      }
    } catch (error) {
      return { context: null, error: makeError("context_failed", "AudioContext creation failed.", { cause: String(error && error.message || error) }), resumePromise: null };
    }
  }
  function installContextListener(ctx) {
    var handler = function () { handleContextState(ctx); };
    try {
      if (typeof ctx.addEventListener === "function") ctx.addEventListener("statechange", handler);
      else ctx.onstatechange = handler;
    } catch (_) { /* State checks also occur around every asynchronous wait. */ }
    ctx.__acFrequencyPitchStateHandler = handler;
  }
  function removeContextListener(ctx) {
    if (!ctx || !ctx.__acFrequencyPitchStateHandler) return;
    try {
      if (typeof ctx.removeEventListener === "function") ctx.removeEventListener("statechange", ctx.__acFrequencyPitchStateHandler);
      else if (ctx.onstatechange === ctx.__acFrequencyPitchStateHandler) ctx.onstatechange = null;
    } catch (_) { /* A closing context may reject listener cleanup. */ }
  }
  function handleContextState(ctx) {
    if (ctx !== context) return;
    lastContextState = ctx.state || "suspended";
    if (lastContextState === "closed") {
      context = null;
      resetDeviceRate(true);
      if (releasing) disconnectVoice(releasing, true);
      if (active && active.status === "playing") {
        stopPlaying(active, "context_interrupted", true);
      } else if (active && active.hasReachedRunning) {
        failStarting(active, "context_interrupted", { contextState: "closed" });
      }
      return;
    }
    if (lastContextState !== "running") {
      if (releasing) disconnectVoice(releasing, true);
      if (active && active.status === "playing") {
        stopPlaying(active, "context_interrupted", true);
      } else if (active && active.hasReachedRunning) {
        failStarting(active, "context_interrupted", { contextState: lastContextState });
      }
    }
  }
  function waitForRunning(playback, ctx, resumePromise) {
    return new Promise(function (resolve) {
      var finished = false;
      var timer = null;
      function cleanup() {
        if (timer !== null) clearTimer(timer);
        playback.resumeTimer = null;
        if (ctx && typeof ctx.removeEventListener === "function") {
          try { ctx.removeEventListener("statechange", stateHandler); } catch (_) {}
        }
        playback.cancelWait = null;
      }
      function finish(value) {
        if (finished) return;
        finished = true;
        cleanup();
        resolve(value);
      }
      function stateHandler() {
        if (ctx.state === "running") finish({ ok: true });
        else if (ctx.state === "closed") finish({ ok: false, code: "context_interrupted", contextState: "closed" });
      }
      if (!ctx) {
        finish({ ok: false, code: "context_failed" });
        return;
      }
      if (ctx.state === "running") {
        finish({ ok: true });
        return;
      }
      playback.cancelWait = function () {
        finish({ ok: false, code: "cancelled", contextState: ctx.state });
      };
      if (typeof ctx.addEventListener === "function") ctx.addEventListener("statechange", stateHandler);
      timer = setTimer(function () { finish({ ok: false, code: "playback_blocked", timedOut: true, contextState: ctx.state }); }, LIMITS.resumeTimeoutMs);
      playback.resumeTimer = timer;
      if (resumePromise && typeof resumePromise.then === "function") {
        Promise.resolve(resumePromise).then(function () {
          if (ctx.state === "running") finish({ ok: true });
        }, function (error) {
          finish({ ok: false, code: "playback_blocked", contextState: ctx.state, cause: error });
        });
      }
    });
  }
  function waitForRelease(playback) {
    if (!releasing) return Promise.resolve();
    playback.waitingForRelease = true;
    return releasing.done.then(function () { playback.waitingForRelease = false; });
  }
  function createVoice(playback) {
    var ctx = context;
    var nowTime = finite(ctx.currentTime) ? ctx.currentTime : 0;
    var voice = {
      client: playback.client,
      playbackId: playback.playbackId,
      sourceId: playback.sourceId,
      kind: playback.kind,
      frequency: playback.kind === "tone" ? playback.frequency : null,
      partialCount: playback.kind === "tone" ? 1 : playback.partials.length,
      oscillators: [],
      nodes: [],
      partialGains: [],
      envelope: null,
      master: null,
      disconnected: false,
      endedCount: 0,
      releaseTimer: null,
      done: null,
      resolveDone: null,
      autoTimer: null
    };
    playback.voice = voice;
    voice.done = new Promise(function (resolve) { voice.resolveDone = resolve; });
    voice.envelope = ctx.createGain();
    voice.envelope.__acFrequencyPitchRole = "envelope";
    voice.envelope.gain.setValueAtTime(0, nowTime);
    ramp(voice.envelope.gain, 0, 1, LIMITS.attackMs, nowTime);
    voice.nodes.push(voice.envelope);
    voice.master = ctx.createGain();
    voice.master.__acFrequencyPitchRole = "master";
    voice.master.gain.setValueAtTime(volumeGain(playback.client.volume), nowTime);
    voice.nodes.push(voice.master);
    voice.envelope.connect(voice.master);
    voice.master.connect(ctx.destination);
    var parts = playback.kind === "tone"
      ? [{ frequency: playback.frequency, gain: 1 }]
      : playback.partials;
    parts.forEach(function (part) {
      var oscillator = ctx.createOscillator();
      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(part.frequency, nowTime);
      var partialGain = ctx.createGain();
      partialGain.__acFrequencyPitchRole = "partial";
      partialGain.gain.setValueAtTime(part.gain, nowTime);
      oscillator.connect(partialGain);
      partialGain.connect(voice.envelope);
      oscillator.onended = function () {
        if (voice.disconnected || releasing !== voice) return;
        voice.endedCount += 1;
        if (voice.endedCount >= voice.oscillators.length) disconnectVoice(voice, false);
      };
      voice.oscillators.push(oscillator);
      voice.partialGains.push(partialGain);
      voice.nodes.push(oscillator, partialGain);
    });
    voice.oscillators.forEach(function (oscillator) { oscillator.start(nowTime); });
    return voice;
  }
  function disconnectVoice(voice, immediate) {
    if (!voice || voice.disconnected) return;
    voice.disconnected = true;
    if (voice.releaseTimer !== null) {
      clearTimer(voice.releaseTimer);
      voice.releaseTimer = null;
    }
    voice.nodes.forEach(function (node) {
      try { if (node && typeof node.disconnect === "function") node.disconnect(); } catch (_) {}
    });
    if (releasing === voice) releasing = null;
    if (voice.resolveDone) voice.resolveDone();
    if (active && active.waitingForRelease) active.waitingForRelease = false;
  }
  function discardVoice(voice) {
    if (!voice || voice.disconnected) return;
    voice.disconnected = true;
    voice.oscillators.forEach(function (oscillator) {
      try { oscillator.stop(); } catch (_) {}
    });
    voice.nodes.forEach(function (node) {
      try { if (node && typeof node.disconnect === "function") node.disconnect(); } catch (_) {}
    });
    if (voice.resolveDone) voice.resolveDone();
  }
  function beginRelease(playback, reason, notify, replacedBy, immediate) {
    var voice = playback.voice;
    if (!voice || voice.disconnected) return;
    if (playback.autoTimer !== null) {
      clearTimer(playback.autoTimer);
      playback.autoTimer = null;
    }
    if (active === playback) active = null;
    playback.status = "stopping";
    voice.reason = reason;
    releasing = voice;
    var start = now();
    if (immediate || !context || context.state !== "running") {
      disconnectVoice(voice, true);
    } else {
      ramp(voice.envelope.gain, finite(voice.envelope.gain.value) ? voice.envelope.gain.value : 1, 0, LIMITS.releaseMs, start);
      var stopAt = start + LIMITS.releaseMs / 1000;
      voice.oscillators.forEach(function (oscillator) {
        try { oscillator.stop(stopAt); } catch (_) {}
      });
      voice.releaseTimer = setTimer(function () { disconnectVoice(voice, false); }, LIMITS.releaseDisconnectTimeoutMs);
    }
    if (notify && !playback.client.destroyed) {
      playbackEvent(playback, "stopped", {
        reason: reason,
        error: null,
        replacedBy: replacedBy || null
      });
    }
  }
  function stopPlaying(playback, reason, immediate, notify) {
    if (active !== playback || playback.status !== "playing") return false;
    beginRelease(playback, reason, notify !== false, null, immediate);
    return true;
  }
  function stopReasonFailure(playback, reason, details) {
    failStarting(playback, reason, details);
  }
  async function continuePlayback(playback) {
    if (!isCurrent(playback)) return;
    var ctx = playback.context;
    if (!ctx) {
      failStarting(playback, "context_failed");
      return;
    }
    var running = await waitForRunning(playback, ctx, playback.resumePromise);
    if (!isCurrent(playback)) return;
    if (!running.ok) {
      stopReasonFailure(playback, running.code || "playback_blocked", {
        contextState: running.contextState || ctx.state,
        timedOut: !!running.timedOut,
        cause: running.cause ? String(running.cause.message || running.cause) : undefined
      });
      return;
    }
    playback.hasReachedRunning = true;
    if (releasing) await waitForRelease(playback);
    if (!isCurrent(playback)) return;
    if (!context || context !== ctx || ctx.state !== "running") {
      failStarting(playback, "context_interrupted", { contextState: ctx.state });
      return;
    }
    var actualRate;
    try { actualRate = ctx.sampleRate; } catch (error) {
      failStarting(playback, "context_error", { cause: String(error && error.message || error) });
      return;
    }
    if (!finite(actualRate) || actualRate <= 0) {
      failStarting(playback, "context_error", { cause: "AudioContext returned an invalid sampleRate." });
      return;
    }
    setDeviceRate(actualRate);
    var limit = Math.min(LIMITS.maxFrequency, LIMITS.nyquistRatio * actualRate);
    var frequencies = frequencyList(playback);
    for (var i = 0; i < frequencies.length; i += 1) {
      if (frequencies[i] > limit) {
        failStarting(playback, "above_device_limit", {
          limit: limit, sampleRate: actualRate,
          partialIndex: playback.kind === "partials" ? i : undefined
        });
        return;
      }
    }
    try {
      var voice = createVoice(playback);
      if (!isCurrent(playback)) {
        discardVoice(voice);
        return;
      }
      playback.status = "playing";
      playback.waitingForRelease = false;
      playback.autoTimer = setTimer(function () {
        if (active === playback && playback.status === "playing") stopPlaying(playback, "auto_stop", false);
      }, LIMITS.autoStopMs);
      var started = {
        ok: true,
        playbackId: playback.playbackId,
        sourceId: playback.sourceId,
        kind: playback.kind,
        frequency: playback.kind === "tone" ? playback.frequency : null,
        partialCount: playback.kind === "partials" ? playback.partials.length : 1,
        sampleRate: actualRate
      };
      playback.successResult = started;
      playbackEvent(playback, "started", {}, function () { resolvePlayback(playback, started); });
    } catch (error) {
      discardVoice(playback.voice);
      failStarting(playback, "context_error", { cause: String(error && error.message || error) });
    }
  }
  function startRequest(client, kind, request) {
    var playbackId = ++nextPlaybackId;
    var validation = validatePlay(kind, request);
    var partialPlayback = validation.playback;
    partialPlayback.playbackId = playbackId;
    partialPlayback.client = client;
    if (client.destroyed) {
      return Promise.resolve(makePlayFailure(partialPlayback, "validation", "client_destroyed", "Client has been destroyed."));
    }
    if (validation.error) {
      var validationFailure = {
        ok: false,
        playbackId: playbackId,
        sourceId: partialPlayback.sourceId,
        kind: kind,
        stage: "validation",
        error: validation.error
      };
      return Promise.resolve(validationFailure);
    }
    if (!isSupported()) {
      return Promise.resolve(makePlayFailure(partialPlayback, "validation", "unsupported", "Web Audio is unavailable."));
    }

    var previous = active;
    var replacedBy = { clientId: client.id, playbackId: playbackId };
    if (previous) {
      if (previous.status === "playing") {
        beginRelease(previous, "replaced", true, replacedBy, false);
      } else {
        cancelStarting(previous, "superseded", replacedBy);
      }
    }

    var prepared = makeContext();
    var playback = Object.assign(partialPlayback, {
      volumeAtStart: client.volume,
      status: "starting",
      terminal: false,
      settled: false,
      resolve: null,
      resumeTimer: null,
      autoTimer: null,
      context: prepared.context,
      contextError: prepared.error,
      resumePromise: prepared.resumePromise,
      hasReachedRunning: false,
      waitingForRelease: !!releasing,
      voice: null
    });
    active = playback;
    var promise = new Promise(function (resolve) { playback.resolve = resolve; });
    playbackEvent(playback, "starting");
    if (prepared.error) {
      queueMicrotaskSafe(function () {
        if (!isCurrent(playback)) return;
        var code = prepared.error.code === "context_failed" ? "context_failed" : prepared.error.code;
        failStarting(playback, code, prepared.error.cause ? { cause: prepared.error.cause } : {});
      });
    } else {
      continuePlayback(playback).catch(function (error) {
        if (isCurrent(playback)) {
          failStarting(playback, "context_error", { cause: String(error && error.message || error) });
        }
      });
    }
    return promise;
  }
  function createClient(options) {
    options = options || {};
    if (!isSupported()) return resultError("unsupported", "Web Audio is unavailable.");
    if (!finite(options.volume) || options.volume < 0 || options.volume > 100) {
      return resultError("invalid_volume", "Volume must be a finite number from 0 to 100.");
    }
    if (typeof options.onChange !== "function") return resultError("invalid_callback", "onChange must be a function.");
    var client = {
      id: "client-" + (++nextClientId),
      volume: options.volume,
      onChange: options.onChange,
      destroyed: false
    };
    function destroyedError() { return resultError("client_destroyed", "Client has been destroyed."); }
    var api = {
      id: client.id,
      playTone: function (request) {
        if (client.destroyed) {
          var id = ++nextPlaybackId;
          var sourceId = request && typeof request.sourceId === "string" ? request.sourceId : null;
          return Promise.resolve({
            ok: false, playbackId: id, sourceId: sourceId, kind: "tone", stage: "validation",
            error: makeError("client_destroyed", "Client has been destroyed.")
          });
        }
        return startRequest(client, "tone", request);
      },
      playPartials: function (request) {
        if (client.destroyed) {
          var id = ++nextPlaybackId;
          var sourceId = request && typeof request.sourceId === "string" ? request.sourceId : null;
          return Promise.resolve({
            ok: false, playbackId: id, sourceId: sourceId, kind: "partials", stage: "validation",
            error: makeError("client_destroyed", "Client has been destroyed.")
          });
        }
        return startRequest(client, "partials", request);
      },
      retune: function (request) {
        if (client.destroyed) return { ok: false, playbackId: request && request.playbackId, stopped: false, error: makeError("client_destroyed", "Client has been destroyed.") };
        if (!request || typeof request !== "object" || Array.isArray(request) ||
            !Number.isInteger(request.playbackId) || !finite(request.frequency)) {
          return { ok: false, playbackId: request && request.playbackId, stopped: false, error: makeError("invalid_request", "Retune requires a playbackId and finite frequency.") };
        }
        if (!active || active.client !== client) return { ok: false, playbackId: request.playbackId, stopped: false, error: makeError("not_owner", "This client does not own the active sound.") };
        if (active.playbackId !== request.playbackId) return { ok: false, playbackId: request.playbackId, stopped: false, error: makeError("stale_playback", "Playback ID is no longer current.") };
        if (active.kind !== "tone") return { ok: false, playbackId: request.playbackId, stopped: false, error: makeError("not_tone", "Only tone playback can be retuned.") };
        if (active.status !== "playing") return { ok: false, playbackId: request.playbackId, stopped: false, error: makeError("not_started", "Playback has not started.") };
        var check = checkFrequency(request.frequency);
        if (!check.ok) {
          stopPlaying(active, "unplayable", false);
          return { ok: false, playbackId: request.playbackId, stopped: true, error: check.error };
        }
        try {
          var current = active;
          var param = current.voice.oscillators[0].frequency;
          var at = now();
          var from = finite(param.value) ? param.value : current.frequency;
          ramp(param, from, request.frequency, LIMITS.retuneMs, at);
          current.frequency = request.frequency;
          current.voice.frequency = request.frequency;
          playbackEvent(current, "retuned", { frequency: request.frequency });
          return { ok: true, playbackId: current.playbackId, frequency: request.frequency };
        } catch (error) {
          return { ok: false, playbackId: request.playbackId, stopped: false, error: makeError("context_error", "Retuning failed.", { cause: String(error && error.message || error) }) };
        }
      },
      stop: function (request) {
        if (client.destroyed) return { ok: false, playbackId: null, stopped: false, error: makeError("client_destroyed", "Client has been destroyed.") };
        if (request !== undefined && (request === null || typeof request !== "object" || Array.isArray(request) ||
            (request.playbackId !== undefined && (!Number.isInteger(request.playbackId) || request.playbackId < 1)))) {
          return { ok: false, playbackId: null, stopped: false, error: makeError("invalid_request", "Stop request must contain an optional positive playbackId.") };
        }
        var requestedId = request && request.playbackId !== undefined ? request.playbackId : null;
        if (!active || active.client !== client || (requestedId !== null && requestedId !== active.playbackId)) {
          return { ok: true, playbackId: requestedId, stopped: false };
        }
        var current = active;
        if (current.status === "starting") {
          cancelStarting(current, "cancelled");
          return { ok: true, playbackId: current.playbackId, stopped: true };
        }
        stopPlaying(current, "user", false);
        return { ok: true, playbackId: current.playbackId, stopped: true };
      },
      setVolume: function (volume) {
        if (client.destroyed) return resultError("client_destroyed", "Client has been destroyed.");
        if (!finite(volume) || volume < 0 || volume > 100) return resultError("invalid_volume", "Volume must be a finite number from 0 to 100.");
        client.volume = volume;
        var applied = !!(active && active.client === client && (active.status === "starting" || active.status === "playing"));
        if (applied && active.status === "playing" && active.voice) {
          var param = active.voice.master.gain;
          var from = finite(param.value) ? param.value : volumeGain(volume);
          ramp(param, from, volumeGain(volume), LIMITS.attackMs, now());
        }
        return success({ volume: volume, applied: applied });
      },
      getPlayback: function () {
        if (client.destroyed) return resultError("client_destroyed", "Client has been destroyed.");
        if (!active || active.client !== client) return success({ playback: null });
        return success({
          playback: {
            playbackId: active.playbackId,
            sourceId: active.sourceId,
            kind: active.kind,
            frequency: active.kind === "tone" ? active.frequency : null,
            partialCount: active.kind === "partials" ? active.partials.length : 1,
            status: active.status
          }
        });
      },
      destroy: function () {
        if (client.destroyed) return { ok: true };
        client.destroyed = true;
        client.onChange = null;
        eventQueue = eventQueue.filter(function (item) { return item.client !== client; });
        if (active && active.client === client) {
          if (active.status === "starting") {
            var pending = active;
            markTerminal(pending);
            active = null;
            resolvePlayback(pending, makePlayFailure(pending, "startup", "client_destroyed", "Client has been destroyed."));
          } else if (active.status === "playing") {
            var playing = active;
            beginRelease(playing, "client_destroyed", false, null, false);
            if (playing.successResult) resolvePlayback(playing, playing.successResult);
          }
        }
        clients.delete(client.id);
        return { ok: true };
      }
    };
    client.api = Object.freeze(api);
    clients.set(client.id, client);
    return success({ client: client.api, device: deviceSnapshot() });
  }
  function getState() {
    var current = active;
    var status = current ? current.status : (releasing ? "stopping" : "idle");
    var activeFields = current ? {
      clientId: current.client.id,
      playbackId: current.playbackId,
      sourceId: current.sourceId,
      kind: current.kind,
      frequency: current.kind === "tone" ? current.frequency : null,
      partialCount: current.kind === "partials" ? current.partials.length : 1
    } : {
      clientId: null, playbackId: null, sourceId: null, kind: null, frequency: null, partialCount: 0
    };
    var releasingFields = releasing ? Object.freeze({
      clientId: releasing.client.id,
      playbackId: releasing.playbackId,
      sourceId: releasing.sourceId,
      kind: releasing.kind
    }) : null;
    var device = deviceSnapshot();
    return Object.freeze(Object.assign({
      status: status,
      waitingForRelease: !!(current && current.status === "starting" && !!releasing),
      releasing: releasingFields,
      sampleRate: device.sampleRate,
      lastKnownSampleRate: lastKnownSampleRate,
      provisional: device.provisional,
      maxPlayableFrequency: device.maxPlayableFrequency,
      estimatedDeviceLimit: device.estimatedDeviceLimit,
      contextState: context ? context.state : lastContextState
    }, activeFields));
  }
  function installPageLifecycle() {
    if (visibilityInstalled) return;
    visibilityInstalled = true;
    var onPageHide = function () { stopForPageHidden(); };
    var onVisibility = function () {
      if (root.document && root.document.visibilityState === "hidden") stopForPageHidden();
    };
    try { if (typeof root.addEventListener === "function") root.addEventListener("pagehide", onPageHide); } catch (_) {}
    try {
      if (root.document && typeof root.document.addEventListener === "function") {
        root.document.addEventListener("visibilitychange", onVisibility);
      }
    } catch (_) {}
  }
  function stopForPageHidden() {
    if (!active) return;
    if (active.status === "starting") cancelStarting(active, "page_hidden");
    else if (active.status === "playing") stopPlaying(active, "page_hidden", false);
  }

  installPageLifecycle();
  root.AudiocolourFrequencyPitchAudio = Object.freeze({
    version: "1.0.0",
    limits: LIMITS,
    isSupported: isSupported,
    checkFrequency: checkFrequency,
    createClient: createClient,
    getState: getState
  });
}(globalThis));
